"use client";

import React, { useEffect } from "react";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import { useRouter } from "next/navigation";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    // Log the error to console or external error trackers
    console.error("❌ [Root Error Boundary] Unhandled runtime error:", error);
  }, [error]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center space-y-6 transform hover:scale-[1.01] transition-transform duration-300">
        {/* Animated Error Icon */}
        <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center animate-bounce">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>

        {/* Text Details */}
        <div className="space-y-2">
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            Oops! Something went wrong
          </h1>
          <p className="text-sm text-gray-500 font-medium leading-relaxed">
            An unexpected error occurred during page rendering. Our team has been notified.
          </p>
          {error.message && (
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-left">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                Error Details
              </span>
              <p className="text-xs text-red-600 font-mono break-all max-h-24 overflow-y-auto leading-relaxed">
                {error.message}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            onClick={() => reset()}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-[#0f9648] hover:bg-[#0c7d3c] text-white text-sm font-bold rounded-xl transition-all duration-200 shadow-md active:scale-[0.98] cursor-pointer"
          >
            <RefreshCw className="w-4 h-4 animate-spin-hover" />
            Try Again
          </button>
          <button
            onClick={() => {
              reset();
              router.push("/");
            }}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-xl transition-all duration-200 active:scale-[0.98] cursor-pointer"
          >
            <Home className="w-4 h-4" />
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}
